import { Fragment, useCallback, useEffect, useState } from 'react'
import BottomBar from 'views/find-art/components/BottomBar'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { IndividualFindArtFormData } from '../../types'
import { yupResolver } from '@hookform/resolvers/yup'
import { IndividualFindArtFormDataSchemas } from 'views/find-art/schemas'
import useFormErrorFocus from 'shared/hooks/useFormErrorFocus'
import RoomTypeStep from 'views/find-art/components/Quiz/RoomTypeStep'
import StyleStep from 'views/find-art/components/Quiz/StyleStep'
import useAuth from 'hooks/authentication/useAuth'
import useFindArtSteps from 'views/find-art/hooks/useFindArtSteps'
import useCustomMutation from 'shared/hooks/useCustomMutation'
import getFindArtResults from 'views/find-art/services/getFindArtResults'
import { notifyError } from 'utils/toast'
import { hexToRgb } from 'views/find-art/utils'
import { inchToCm } from 'utils/measurement'
import { trackSegmentCustomEvent } from 'shared/services/analytics/segment'
import { IndividualBudgetType, MAX_BUDGET, MAX_SIZE, MIN_BUDGET, MIN_SIZE, SIZE_TYPES } from 'views/find-art/config'
import dynamic from 'next/dynamic'
import { useConfirmModal } from 'hooks/common/useConfirmModal'
import { useRouter } from 'next/router'
import CloseButton from 'shared/components/button/CloseButton'
import useScrollDirectionForWindow from 'hooks/useScrollDirectionForWindow'
import classNames from 'classnames'

const ColorStep = dynamic(() => import('views/find-art/components/Quiz/ColorStep'), { ssr: false })
const BudgetStep = dynamic(() => import('views/find-art/components/Quiz/BudgetStep'), { ssr: false })
const SizeStep = dynamic(() => import('views/find-art/components/Quiz/SizeStep'), { ssr: false })
const FinalScreen = dynamic(() => import('views/find-art/components/FinalScreen'), { ssr: false })
const ResultScreen = dynamic(() => import('views/find-art/components/ResultScreen'), { ssr: false })

const TOTAL_STEPS = 5
const Quiz = () => {
  const [isFetchingResults, setIsFetchingResults] = useState(false)
  const router = useRouter()
  const { authUser } = useAuth()
  const {
    isReachingEnd,
    setIsReachingEnd,
    iter,
    setIter,
    uniqueId,
    setUniqueId,
    artworks,
    setArtworks,
    stepIndex,
    setStepIndex,
    isFinalSreenVisible,
    isResultScreenVisible,
    onShowResults,
    showFinalScreen,
    shouldShowSteps,
  } = useFindArtSteps()

  const showConfirmModal = useConfirmModal()
  const scrollDirectionWindow = useScrollDirectionForWindow()

  const shouldHideHeader = scrollDirectionWindow === 'down'

  const formMethods = useForm<IndividualFindArtFormData>({
    defaultValues: {
      artColors: [],
      artStyles: [],
      size: SIZE_TYPES.map((size) => ({ ...size, isSelected: false })),
      budget: IndividualBudgetType.map((budget) => ({ ...budget, isSelected: false })),
      isOtherCustomBudget: false,
    },
    resolver: yupResolver(
      IndividualFindArtFormDataSchemas[stepIndex],
    ) as unknown as Resolver<IndividualFindArtFormData>,
    shouldUnregister: false,
  })

  const { mutate: getResults, isLoading: isSubmiting } = useCustomMutation(getFindArtResults, {
    onSuccess: (result) => {
      if (result?.uniqueId) setUniqueId(result?.uniqueId)
      if (result?.artworks && result?.artworks.length > 0) {
        setArtworks((prev) => [...prev, ...result.artworks])
        setIter((prev) => prev + 1)
      }
      if (result?.artworks && result?.artworks.length === 0) {
        setIsReachingEnd(true)
      }

      if (authUser) {
        const data = formMethods.getValues()
        const resultArtworkIds = result.artworks.map((artwork) => artwork.id)

        const { artColors, artStyles, budget, size, roomType } = data

        trackSegmentCustomEvent('find_art_results_viewed', {
          findArtType: 'individual',
          email: authUser.email,
          fullName: authUser.fullName,
          artColors,
          artStyles,
          budget: budget.map((budget) => budget.label),
          findArtResult: resultArtworkIds,
          roomType,
          artSize: size.map((size) => size.label),
        })
      }

      onShowResults()
      setIsFetchingResults(false)
    },

    onError: (e) => {
      notifyError(e)
      setIsFetchingResults(false)
    },
  })

  const requestToQuitFindArt = useCallback(() => {
    showConfirmModal(
      {
        title: 'EXIT FIND ART QUIZ?',
        description: isResultScreenVisible ? (
          <>
            Are you sure you want to exit the quiz? All progress will be lost and you will be redirected to the
            discovery page.
            <br />
            <br />
            We recommend you to save the artwork you liked on this page to your moodboard first.
          </>
        ) : (
          <>
            Are you sure you want to exit the quiz? All progress will be lost and you will be redirected to the
            discovery page.
          </>
        ),
        confirmBtnText: 'Exit Quiz',
        confirmButtonVariant: 'warning',
        cancelBtnText: 'Cancel',
      },
      {
        onConfirm: () => router.push('/discover'),
        disableBackdropClick: false,
      },
    )
  }, [showConfirmModal, isResultScreenVisible, router])

  const getResultsWrapper = useCallback(
    async (data: IndividualFindArtFormData, uniqueId: string | undefined, iter: number | undefined) => {
      if (!authUser) return // should not happen since this function only be called when user is logged in

      setIsFetchingResults(true)
      // get results
      await getResults({
        artworkStyles: data.artStyles,
        artworkColors: data.artColors.map(hexToRgb),
        budget: data.budget
          .filter((budget) => budget.isSelected)
          .map((budget) => {
            const min = budget.isOther ? data.customBudget.min : budget.min
            const max = budget.isOther ? data.customBudget.max : budget.max
            return {
              label: budget.label,
              min: min || MIN_BUDGET,
              max: max || MAX_BUDGET,
            }
          }),
        size: data.size
          .filter((size) => size.isSelected)
          .map((size) => {
            const min = size.value.split('-')[0]?.trim()
            const max = size.value.split('-')[1]?.trim()
            const sizeLabel = size.label.substring(0, size.label.indexOf('(')).trim()
            return {
              label: sizeLabel,
              min: min === '0' ? MIN_SIZE : inchToCm(parseInt(min)),
              max: max === '0' ? MAX_SIZE : inchToCm(parseInt(max)),
            }
          }),
        type: 'Individual',
        uniqueId,
        iter,
      })
    },
    [authUser, getResults],
  )

  const submitAndShowResults = useCallback(
    async (data: IndividualFindArtFormData) => {
      await getResultsWrapper(data, undefined, undefined)
    },
    [getResultsWrapper],
  )

  const nextStep = async () => {
    const isStepValid = await formMethods.trigger()
    if (isStepValid) {
      setStepIndex((currenIndex) => Math.min(currenIndex + 1, TOTAL_STEPS - 1))
    }
  }

  const prevStep = () => {
    // we don't need to validate the form when going back
    setStepIndex((currenIndex) => Math.max(currenIndex - 1, 0))
  }

  const loadMoreArtworks = async () => {
    if (uniqueId) {
      await getResultsWrapper(formMethods.getValues(), uniqueId, iter + 1)
    }
  }

  const onSkip = () => {
    switch (stepIndex) {
      case 0: // room type step - required
        break
      case 1: // style step - required
        break
      case 2: // color step - optional
        // reset the color
        formMethods.setValue('artColors', [])
        nextStep()
        break
      case 3: // size step - optional
        // reset the size
        formMethods.setValue(
          'size',
          SIZE_TYPES.map((size) => ({ ...size, isSelected: false })),
        )
        nextStep()
        break
      case 4: // budget step - optional
        // reset the budget
        formMethods.setValue(
          'budget',
          IndividualBudgetType.map((budget) => ({ ...budget, isSelected: false })),
        )
        nextStep()
        break
      default:
        break
    }
  }

  useFormErrorFocus(formMethods)

  useEffect(() => {
    formMethods.trigger() // trigger form validation when step changes
    window.scrollTo({ top: 0, behavior: 'instant' }) // scroll to top when step changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex])

  return (
    <>
      {/* header */}
      <div
        className={classNames(
          'fixed left-0 top-0 z-10 inline-flex h-[80px] w-full items-center justify-between gap-2',
          'border-b border-black/10 bg-[#fdfdfd] px-4 shadow lg:px-6',
          shouldHideHeader && 'hidden',
        )}
      >
        <button onClick={requestToQuitFindArt} className="font-beni text-3xl uppercase lg:text-5xl">
          Cohart
        </button>
        <h1 className="w-full flex-1 text-center text-[16px] font-semibold leading-[1.4] text-kokushoku-black lg:text-[20px]">
          Find Art Quiz
        </h1>
        <CloseButton iconClassName="size-5 lg:size-6" onClose={requestToQuitFindArt} />
      </div>

      {/* quiz */}
      <Fragment>
        <FormProvider {...formMethods}>
          {/* should show steps */}
          {shouldShowSteps && (
            <>
              {/* content */}
              <div className="w-full px-4 pb-[150px] empty:hidden">
                <form className="flex-1">
                  {stepIndex === 0 && <RoomTypeStep onNext={nextStep} />}
                  {stepIndex === 1 && <StyleStep onNext={nextStep} />}
                  {stepIndex === 2 && <ColorStep onNext={nextStep} />}
                  {stepIndex === 3 && <SizeStep onNext={nextStep} />}
                  {stepIndex === 4 && <BudgetStep onDone={showFinalScreen} />}
                </form>
              </div>
              {/* bottom bar */}
              <div className="fixed bottom-0 left-0 w-full border-t-2 border-black/10 bg-white px-6 lg:px-8">
                <BottomBar
                  totalSteps={TOTAL_STEPS}
                  stepIndex={stepIndex}
                  onNextStep={nextStep}
                  onPrevStep={prevStep}
                  onCancel={requestToQuitFindArt}
                  onDone={showFinalScreen}
                  isSubmiting={isSubmiting}
                  canGoNext={formMethods.formState.isValid}
                  onSkip={onSkip}
                />
              </div>
            </>
          )}

          {/* final screen */}
          {isFinalSreenVisible && (
            <FinalScreen onShowResults={formMethods.handleSubmit(submitAndShowResults)} loading={isFetchingResults} />
          )}

          {/* result screen */}
          {isResultScreenVisible && (
            <ResultScreen
              artworks={artworks}
              loadMore={loadMoreArtworks}
              isReachingEnd={isReachingEnd}
              isLoading={isFetchingResults}
            />
          )}
        </FormProvider>
      </Fragment>
    </>
  )
}

export default Quiz
